import { ChangeEvent } from "react";

import { Client } from "@/Common/Api/Client";
import { Email as ModelsCommonResponseEmail } from "@/Common/Api/Contacts/Dto/Email";
import { FlatLookupView } from "@/Common/Api/MedfarDictionaries/Dto/Responses/FlatLookupView";
import { AsyncLoaderControl } from "@/Common/Components/Loader/AsyncLoaderControl";
import { Patient } from "@/Common/Models/Patient/Patient";
import { PortalRegistrationState } from "@/Common/Models/Portal/Enums/PortalRegistrationState";
import * as LocalOperations from "@/Common/Operations/operations.local";
import * as ServerOperations from "@/Common/Operations/operations.server";
import { LocalEvent } from "@/Common/Stores/Actions/LocalEvent";
import { ServerEvent } from "@/Common/Stores/Actions/ServerEvent";
import { ISetRelinkDelegateFunction, IToggleButtonFunction } from "../PortalRegistration";
import { AccountInvitationConfirmation } from "./AccountInvitationConfirmation";

interface IToggleButtonWithEmailDataFunction {
    (isDisabled: boolean, email: string, emailTypeLookupId: string): void;
}

interface IAccountSendInvitationProps {
    patientInfo: Patient;
    setDelegatesFunction?: ISetRelinkDelegateFunction;
    onCloseDelegate?: (registrationState: PortalRegistrationState) => void;
    onSaveSuccessDelegate?: (portalId: string) => void;
    onToggleSendDelegate?: IToggleButtonWithEmailDataFunction;
    onToggleRefuseDelegate?: IToggleButtonFunction;
    onToggleHideActionsDelegate?: IToggleButtonFunction;
}

class RegisterForPortalResponse extends ServerEvent {
    public RegistrationCode: string;
    public PortalId: string;
    public NewEmailAdded: boolean;
}

class AccountSendInvitationState {
    public displayWait: boolean;
    public displayConfirmation: boolean;
    public errors: string[];

    public addresses: EmailAddressEntry[];
    public useCustomAddress: boolean;
    public customAddress: string;
    public registrationCode: string;
    public contactTypes: FlatLookupView[];
    public selectedContactType: FlatLookupView;

    constructor(s: AccountSendInvitationState = null) {
        if (s != null) {
            this.displayWait = s.displayWait;
            this.displayConfirmation = s.displayConfirmation;
            this.errors = s.errors;

            this.addresses = s.addresses;
            this.useCustomAddress = s.useCustomAddress;
            this.customAddress = s.customAddress;
            this.registrationCode = s.registrationCode;
            this.selectedContactType = s.selectedContactType;
            this.contactTypes = s.contactTypes;

            return;
        }

        this.displayWait = false;
        this.displayConfirmation = false;
        this.errors = [];

        this.addresses = [];
        this.useCustomAddress = false;
        this.customAddress = "";
        this.registrationCode = "";
        this.contactTypes = cacheStore.FindLookupsByTypeCode("CONTACTPOINTTYPE");
        this.selectedContactType = this.contactTypes[0];
    }
}

class EmailAddressEntry {
    public id: string;
    public address: string;
    public type: string;
    public typeEn: string;
    public selected: boolean;
    public typeLookupId: string;

    constructor(e: ModelsCommonResponseEmail) {
        const ls = cacheStore.GetLookupById(e.ContactPointTypeLookupId);
        let typeName = "";

        if (ls) {
            typeName = ls.GetValue();
            this.typeEn = ls.en;
        }
        else {
            this.typeEn = "";
        }

        this.id = e.ContactPoint_id;
        this.address = e.Address;
        this.type = typeName;
        this.selected = false;
        this.typeLookupId = e.ContactPointTypeLookupId;
    }
}

class AccountSendInvitation extends React.Component<IAccountSendInvitationProps, AccountSendInvitationState> {
    private emailPattern: RegExp = /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;

    constructor(props: IAccountSendInvitationProps) {
        super(props);

        const initialState = new AccountSendInvitationState(this.state);
        initialState.addresses = props.patientInfo.Emails.map((p: ModelsCommonResponseEmail) => new EmailAddressEntry(p));

        if (initialState.addresses.length > 0) {
            initialState.addresses[0].selected = true;
        }
        else {
            initialState.useCustomAddress = true;
        }

        this.state = initialState;
    }

    public componentDidMount(): void {
        const self = this;

        if (this.props.setDelegatesFunction != null) {
            this.props.setDelegatesFunction(this.sendRegisterRequest.bind(this), this.refusePortal.bind(this));
        }

        // call the toggleSendDelegate
        window.setTimeout(() => {
            if (self.props.onToggleSendDelegate != null) {
                if (!self.state.useCustomAddress)
                    self.props.onToggleSendDelegate(false, self.state.addresses[0].address, self.state.addresses[0].typeLookupId);
                else
                    self.props.onToggleSendDelegate(false, self.state.customAddress, self.state.selectedContactType.LookupID);
            }
        }, 10);
    }

    public render(): JSX.Element {
        if (this.state.displayWait) {
            return this.renderWait();
        }
        else if (this.state.displayConfirmation) {
            return this.renderConfirmation();
        }
        else {
            return this.renderForm();
        }
    }

    private renderForm(): JSX.Element {
        const lang = sessionStore.userLanguageCode;
        let hasEmails = this.props.patientInfo.Emails.length > 0 && this.props.patientInfo.Emails != null;

        return (
            <div className="content">
                {this.state.errors && this.state.errors.length > 0 && (
                    <div style={{ padding: "0 0 20px" }}>
                        {this.state.errors.map((errorMessage, idx) =>
                            <p key={"error_" + idx} className="red-text">{errorMessage}</p>
                        )}
                    </div>
                )}
                <div>
                    {this.state.addresses.map((e: EmailAddressEntry, idx: number) => (
                        <div key={idx + "___n"} className="flex-container space m">
                            <div className="flx-col center a">
                                <input name="email-option" checked={e.selected} onChange={this.setSelected.bind(this, e)} className="pointer" type="radio" />
                            </div>
                            <div className="flx-col b">
                                <div className="flex-container n">
                                    <div className="flx-col g right">
                                        <span>{e.type}</span>
                                    </div>
                                    <div className="flx-col pl14 h">
                                        <span>{e.address}</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    ))}

                    {hasEmails && (
                        <div style={{ padding: "10px 0" }}>
                            <strong>{cacheStore.GetString("OtherLabel")}</strong>
                        </div>
                    )}

                </div>

                <div>
                    <div className="flex-container m">
                        {hasEmails && (
                            <div className="flx-col center a">
                                <input type="radio" onChange={this.setUseCustomEmail.bind(this)} checked={this.state.useCustomAddress} className="pointer" name="email-option" />
                            </div>
                        )}

                        <div className={hasEmails ? "flx-col b" : "flx-col"}>
                            <div className="flex-container pp n">
                                <div className="flx-col g right">
                                    <label>{cacheStore.GetString("TypeLabel")}</label>
                                </div>
                                <div className="flx-col h">
                                    <select onFocus={this.selectCustom.bind(this)} value={this.state.selectedContactType.LookupID} onChange={this.onContactTypeChange.bind(this)} className="myle-control form-control">
                                        {this.state.contactTypes.map((l: FlatLookupView) => {
                                            var label = lang === "en" ? l.en : l.fr;

                                            return <option key={l.LookupID} value={l.LookupID}>{label}</option>;
                                        })}
                                    </select>
                                </div>
                            </div>

                            <div className="flex-container pp n">
                                <div className="flx-col g right">
                                    <label>{cacheStore.GetString("EmailLabel")}</label>
                                </div>
                                <div className="flx-col h">
                                    <input
                                        type="text"
                                        className="form-control"
                                        value={this.state.customAddress}
                                        placeholder={cacheStore.GetString("TypeYourEmail")}
                                        onFocus={() => this.selectCustom()}
                                        onChange={e => this.onCustomEmailChange(e)}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

            </div>
        );
    }

    private renderConfirmation(): JSX.Element {
        return (
            <AccountInvitationConfirmation email={this.selectedAddress().address} registrationCode={this.state.registrationCode} />
        );
    }

    private renderWait(): JSX.Element {
        return (
            <div className="content">
                <h4 className="thin-font">Contacting portal services</h4>

                <div className="clearfix" style={{ padding: "20px 0 0" }}>
                    <AsyncLoaderControl IsActive={true} ClearStyle={true} />
                </div>

            </div>
        );
    }

    private setSelected(item: EmailAddressEntry): void {
        const s = new AccountSendInvitationState(this.state);
        let selectedAddress: string = "";
        s.addresses = s.addresses.map((a: EmailAddressEntry) => {
            a.selected = item.id === a.id;
            if (a.selected)
                selectedAddress = a.address;
            return a;
        });

        s.useCustomAddress = false;
        this.setState(s);

        if (this.props.onToggleSendDelegate != null) {
            this.props.onToggleSendDelegate(false, selectedAddress, s.selectedContactType.LookupID);
        }
    }

    private setUseCustomEmail(): void {
        const s = new AccountSendInvitationState(this.state);

        s.addresses = this.state.addresses.map(a => {
            a.selected = false;
            return a;
        });

        s.useCustomAddress = true;

        if (this.props.onToggleSendDelegate != null) {
            if (s.useCustomAddress && this.emailPattern.test(s.customAddress) === false)
                this.props.onToggleSendDelegate(true, s.customAddress, s.selectedContactType.LookupID);
            else
                this.props.onToggleSendDelegate(false, s.customAddress, s.selectedContactType.LookupID);
        }

        this.setState(s);
    }

    private onCustomEmailChange(e: ChangeEvent<HTMLInputElement>): void {
        const val = e.target.value;
        const s = new AccountSendInvitationState(this.state);
        s.customAddress = val;
        this.setState(s);

        if (this.props.onToggleSendDelegate != null) {
            if (s.useCustomAddress && this.emailPattern.test(s.customAddress) === false)
                this.props.onToggleSendDelegate(true, s.customAddress, s.selectedContactType.LookupID);
            else
                this.props.onToggleSendDelegate(false, s.customAddress, s.selectedContactType.LookupID);
        }
    }

    private selectCustom(): void {
        this.setUseCustomEmail();
    }

    private refusePortal(): void {
        const s = new AccountSendInvitationState(this.state);
        s.displayWait = true;
        this.setState(s);

        if (this.props.onToggleRefuseDelegate != null)
            this.props.onToggleRefuseDelegate(true);

        if (this.props.onToggleSendDelegate != null)
            this.props.onToggleSendDelegate(true, s.customAddress, s.selectedContactType.LookupID);

        const client = new Client();

        client.Post(
            ServerOperations.Server.Portal_Proxy_Refuse,
            {
                PatientId: this.props.patientInfo.IndividualResponse.Contact_id
            },
            this.onRefuseSuccess.bind(this),
            this.onRefuseError.bind(this)
        );
    }

    private onRefuseSuccess(): void {
        if (this.props.onCloseDelegate != null) {
            this.props.onCloseDelegate(PortalRegistrationState.PortalRefused);
        }

        if (this.props.onToggleRefuseDelegate != null)
            this.props.onToggleRefuseDelegate(false, true);

        if (this.props.onToggleSendDelegate != null)
            this.props.onToggleSendDelegate(false, this.state.customAddress, this.state.selectedContactType.LookupID);
    }

    private onRefuseError(e: any): void {
        console.error(e);
    }

    private sendRegisterRequest(): void {
        const s = new AccountSendInvitationState(this.state);
        s.displayWait = true;
        this.setState(s);

        if (this.props.onToggleRefuseDelegate != null)
            this.props.onToggleRefuseDelegate(true);
        if (this.props.onToggleSendDelegate != null)
            this.props.onToggleSendDelegate(true, this.state.customAddress, s.selectedContactType.LookupID);

        const self = this;
        const client = new Client();
        client.Post(
            ServerOperations.Server.Portal_Proxy_Register,
            {
                Mock: false,
                SimulateNetworkDelay: false,
                PatientId: self.props.patientInfo.IndividualResponse.Contact_id,
                Email: self.selectedAddress().address,
                EmailTypeLookupId: self.selectedAddress().typeLookupId
            },
            this.onSendRegisterSuccess.bind(this),
            this.onSendRegisterError.bind(this)
        );
    }

    private onSendRegisterSuccess(response: RegisterForPortalResponse): void {
        const s = new AccountSendInvitationState(this.state);

        s.displayWait = false;

        if (response.Errors && response.Errors.length) {
            s.displayConfirmation = false;
            s.errors = response.Errors.map(p => p.Message);
            s.registrationCode = null;

            this.setState(s);
            return;
        }

        s.displayConfirmation = true;
        s.errors = [];
        s.registrationCode = response.RegistrationCode;
        this.setState(s);

        const op = new LocalEvent(LocalOperations.Portal.Portal_UpdateInfo);
        op.AddItem("patientContactId", this.props.patientInfo.IndividualResponse.Contact_id);
        op.AddItem("portalProfileId", response.PortalId);
        op.AddItem("portalOverview", null);
        op.AddItem("portalStatus", PortalRegistrationState.RegistrationPending);
        LocalEventDispatcher.dispatch(op);

        if (this.props.onToggleRefuseDelegate != null)
            this.props.onToggleRefuseDelegate(false);
        if (this.props.onToggleSendDelegate != null)
            this.props.onToggleSendDelegate(false, this.state.customAddress, s.selectedContactType.LookupID);
        if (this.props.onSaveSuccessDelegate != null)
            this.props.onSaveSuccessDelegate(response.PortalId);
        if (this.props.onToggleHideActionsDelegate != null)
            this.props.onToggleHideActionsDelegate(true);
    }

    private onSendRegisterError(e: any): void {
        console.error(e);

        const s = new AccountSendInvitationState(this.state);

        s.displayWait = false;
        s.displayConfirmation = false;
        s.errors = [e];
        s.registrationCode = null;

        this.setState(s);
    }

    private selectedAddress(): EmailAddressEntry {
        if (this.state.useCustomAddress) {
            return this.getEmailModelFromCustomForm();
        }
        else {
            const a = this.state.addresses.filter(p => p.selected);
            if (a.length > 0) {
                return a[0];
            }
            else return null;
        }
    }

    private getEmailModelFromCustomForm(): EmailAddressEntry {
        const e = new ModelsCommonResponseEmail();
        e.Address = this.state.customAddress;
        e.ContactPointTypeLookupId = this.state.selectedContactType.LookupID;
        return new EmailAddressEntry(e);
    }

    private onContactTypeChange(e: ChangeEvent<HTMLSelectElement>): void {
        const s = new AccountSendInvitationState(this.state);
        s.selectedContactType = this.state.contactTypes
            .filter(t => t.LookupID === e.target.value)[0];
        this.setState(s);

        if (this.props.onToggleSendDelegate != null) {
            if (s.useCustomAddress && this.emailPattern.test(s.customAddress) === false)
                this.props.onToggleSendDelegate(true, s.customAddress, s.selectedContactType.LookupID);
            else
                this.props.onToggleSendDelegate(false, s.customAddress, s.selectedContactType.LookupID);
        }
    }
}

export { AccountSendInvitation };
